import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { verifyInvestorCredentials, writeInvestorSession } from '../../data/investorAccess';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
interface InvestorAccessScreenProps {
  onGranted: () => void;
}

/** Private entry point for the investor deck. */
export function InvestorAccessScreen({
  onGranted
}: InvestorAccessScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    window.setTimeout(() => {
      const audience = verifyInvestorCredentials(email, password);
      if (audience) {
        writeInvestorSession(remember, audience);
        onGranted();
        return;
      }
      setPending(false);
      setError('Incorrect email or password. Please try again.');
    }, 650);
  };
  return <main className="deck-type relative min-h-screen w-full overflow-hidden bg-[#FAFCF9]" data-id="element-2380" data-mp="21rtvbdolzm9q">
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(ellipse 70% 55% at 50% -10%, rgba(0,200,83,0.09) 0%, rgba(250,252,249,0) 62%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
    }} data-id="element-2381" data-mp="26rpsou2swfgb" />

      {/* Understated communication lines */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-id="element-2382" data-mp="2lhdppj3y8ry8">
        {['M-60 250 C 320 190, 620 330, 1500 210', 'M-60 460 C 380 400, 700 560, 1500 430', 'M-60 690 C 300 640, 760 780, 1500 660'].map((d, i) => <motion.path key={d} d={d} fill="none" stroke="rgba(0,153,64,0.16)" strokeWidth={1} initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 1
      }} transition={{
        duration: 2.6,
        delay: 0.3 + i * 0.35,
        ease
      }} data-id="element-2383" data-mp="zenpesph48tp" data-mpi={i} />)}
        {[280, 500, 720].map((cy, i) => <motion.circle key={cy} r={3} fill="rgba(0,153,64,0.32)" initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.9, 0],
        cx: [-40, 1480],
        cy
      }} transition={{
        duration: 11 + i * 2,
        repeat: Infinity,
        delay: 1.4 + i * 1.6,
        ease: 'linear'
      }} data-id="element-2384" data-mp="1i3c3aupjhdvz" data-mpi={i} />)}
      </svg>

      <div className="relative mx-auto flex min-h-screen w-full max-w-[520px] flex-col justify-center px-6 py-16 sm:px-10" data-id="element-2385" data-mp="3tuftqvy3aqfz">
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2386" data-mp="3owo30596ladd">
          <div className="flex items-center gap-3" data-id="element-2387" data-mp="29akgfeu6zjgy">
            <img src={VEXU_LOGO} alt="" className="h-8 w-8 object-contain" data-id="element-2388" data-mp="29dgh3axfcm93" />
            <span className="text-[17px] font-bold tracking-tight text-[#0B1712]" data-id="element-2389" data-mp="1umfw66fhqzw2">
              Vexu.ai
            </span>
          </div>

          <h1 className="mt-12 text-[38px] font-semibold leading-[1.15] tracking-[-0.025em] text-[#0B1712]" data-id="element-2390" data-mp="1lmb1mwvebvzb">
            Investor Access
          </h1>
          <p className="mt-4 text-[15.5px] leading-relaxed text-[#5A6B60]" data-id="element-2391" data-mp="1qmfhckohcmoh">
            Private access to VEXU investor materials.
          </p>
        </motion.div>

        <motion.form className="mt-12" onSubmit={handleSubmit} noValidate initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.25,
        ease
      }} data-id="element-2392" data-mp="1gm55b3e6llaw">
          <div data-id="element-2393" data-mp="3aylh0jpqxrn5">
            <label htmlFor="investor-email" className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A998F]" data-id="element-2394" data-mp="1ayg712xla3z7">
              Email
            </label>
            <input id="investor-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} disabled={pending} className="mt-3 w-full border-b border-[#0B1712]/15 bg-transparent pb-3 text-[16px] text-[#0B1712] outline-none transition-colors placeholder:text-[#B4BEB8] focus:border-vexu-600 disabled:opacity-60" placeholder="you@firm.com" data-id="element-2395" data-mp="11xiat6te70r6" />
          </div>

          <div className="mt-8" data-id="element-2396" data-mp="e9ifrup1so6s">
            <label htmlFor="investor-password" className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A998F]" data-id="element-2397" data-mp="e6cny7p1b87d">
              Password
            </label>
            <input id="investor-password" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} disabled={pending} className="mt-3 w-full border-b border-[#0B1712]/15 bg-transparent pb-3 text-[16px] text-[#0B1712] outline-none transition-colors placeholder:text-[#B4BEB8] focus:border-vexu-600 disabled:opacity-60" placeholder="••••••••" data-id="element-2398" data-mp="3qbqst2flxhsd" />
          </div>

          <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2399" data-mp="3cldjowqzbx1g">
            <label className="flex cursor-pointer items-center gap-2.5 text-[13px] text-[#5A6B60]" data-id="element-2400" data-mp="3hjot56dd45j5">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} disabled={pending} className="h-[15px] w-[15px] cursor-pointer rounded-[4px] border-[#0B1712]/20 text-vexu-600 accent-[#009940]" data-id="element-2401" data-mp="3m9vij8fp7u2g" />
              Remember me
            </label>
            <button type="button" className="text-[13px] text-[#8A998F] underline-offset-4 transition-colors hover:text-vexu-700 hover:underline" data-id="element-2402" data-mp="2lqs7jj8goggz">
              Forgot password?
            </button>
          </div>

          {error && <motion.p role="alert" className="mt-6 text-[13.5px] text-[#B4543A]" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          ease
        }} data-id="element-2403" data-mp="2qkmzf9y801r5">
              {error}
            </motion.p>}

          <button type="submit" disabled={pending} className="mt-10 w-full rounded-full bg-[#0B1712] py-4 text-[14.5px] font-semibold tracking-[0.02em] text-white transition-all hover:bg-[#12241C] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-2404" data-mp="2cq3vuv6jis79">
            {pending ? 'Accessing…' : 'Access Investor Portal'}
          </button>
        </motion.form>

        <motion.div className="mt-14 border-t border-[#0B1712]/[0.09] pt-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.6,
        ease
      }} data-id="element-2405" data-mp="3queabzrsxtri">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-vexu-700/80" data-id="element-2406" data-mp="3lsf84t7st2w5">
            Confidential · Authorized access only
          </p>
          <p className="mt-3 text-[11.5px] leading-relaxed text-[#A3AFA7]" data-id="element-2407" data-mp="df86kz2ot97c">
            The information contained in this portal is confidential and
            intended solely for authorized recipients.
          </p>
        </motion.div>
      </div>
    </main>;
}